import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// Content kinds match the SQL enum public.content_kind
export const CONTENT_KINDS = [
  "song", "release", "book", "activation", "wisdom", "codex", "gate", "creation", "circle_post",
] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number];
const contentKind = z.enum(CONTENT_KINDS);

// ─── FOLLOWS ────────────────────────────────────────────────────────────

export const isFollowingBuddha = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<boolean> => {
    const { data } = await context.supabase
      .from("follows")
      .select("id")
      .eq("user_id", context.userId)
      .eq("follow_target", "buddha_ka")
      .maybeSingle();
    return Boolean(data);
  });

export const toggleFollowBuddha = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ following: boolean }> => {
    const { data: existing } = await context.supabase
      .from("follows")
      .select("id")
      .eq("user_id", context.userId)
      .eq("follow_target", "buddha_ka")
      .maybeSingle();
    if (existing) {
      const { error } = await context.supabase.from("follows").delete().eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { following: false };
    }
    const { error } = await context.supabase.from("follows").insert({
      user_id: context.userId, follow_target: "buddha_ka",
    });
    if (error) throw new Error(error.message);
    return { following: true };
  });

export const getFollowerCount = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("count_follows", { _target: "buddha_ka" });
  if (error) throw new Error(error.message);
  return { count: (data as number) ?? 0 };
});


// ─── LIKES ──────────────────────────────────────────────────────────────

const contentTarget = z.object({ contentKind, contentRef: z.string().min(1).max(200) });

export const getLikeState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => contentTarget.parse(raw))
  .handler(async ({ data, context }): Promise<{ liked: boolean; count: number }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [likedRes, countRes] = await Promise.all([
      context.supabase
        .from("content_likes")
        .select("id")
        .eq("user_id", context.userId)
        .eq("content_kind", data.contentKind)
        .eq("content_ref", data.contentRef)
        .maybeSingle(),
      supabaseAdmin.rpc("count_content_likes", {
        _kind: data.contentKind,
        _ref: data.contentRef,
      }),
    ]);
    return { liked: Boolean(likedRes.data), count: (countRes.data as number) ?? 0 };
  });


export const toggleLike = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => contentTarget.parse(raw))
  .handler(async ({ data, context }): Promise<{ liked: boolean }> => {
    const { data: existing } = await context.supabase
      .from("content_likes")
      .select("id")
      .eq("user_id", context.userId)
      .eq("content_kind", data.contentKind)
      .eq("content_ref", data.contentRef)
      .maybeSingle();
    if (existing) {
      const { error } = await context.supabase.from("content_likes").delete().eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { liked: false };
    }
    const { error } = await context.supabase.from("content_likes").insert({
      user_id: context.userId,
      content_kind: data.contentKind,
      content_ref: data.contentRef,
    });
    if (error) throw new Error(error.message);
    return { liked: true };
  });

// ─── COMMENTS ───────────────────────────────────────────────────────────

export interface CommentRow {
  id: string;
  parent_id: string | null;
  body: string;
  author_display_name: string | null;
  created_at: string;
}

export const listComments = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => contentTarget.parse(raw))
  .handler(async ({ data }): Promise<CommentRow[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: rows, error } = await supa
      .from("content_comments")
      .select("id, parent_id, body, author_display_name, created_at")
      .eq("content_kind", data.contentKind)
      .eq("content_ref", data.contentRef)
      .eq("is_hidden", false)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const addComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z.object({
      contentKind,
      contentRef: z.string().min(1).max(200),
      body: z.string().trim().min(1).max(4000),
      parentId: z.string().uuid().optional(),
    }).parse(raw)
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("content_comments").insert({
      user_id: context.userId,
      content_kind: data.contentKind,
      content_ref: data.contentRef,
      body: data.body,
      parent_id: data.parentId ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ commentId: z.string().uuid() }).parse(raw))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("content_comments")
      .delete()
      .eq("id", data.commentId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ─── CIRCLES ────────────────────────────────────────────────────────────

export interface Circle {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  hero_image_url: string | null;
  is_public: boolean;
  member_count?: number;
}

export const listCircles = createServerFn({ method: "GET" }).handler(async (): Promise<Circle[]> => {
  const { createClient } = await import("@supabase/supabase-js");
  const supa = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  const { data, error } = await supa
    .from("circles")
    .select("id, slug, title, description, hero_image_url, is_public")
    .eq("is_public", true)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getCircle = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(raw))
  .handler(async ({ data }): Promise<Circle | null> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: c, error } = await supa
      .from("circles")
      .select("id, slug, title, description, hero_image_url, is_public")
      .eq("slug", data.slug)
      .eq("is_public", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return c;
  });

export interface CirclePost {
  id: string;
  title: string | null;
  body: string;
  author_display_name: string | null;
  created_at: string;
}

export const listCirclePosts = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => z.object({ circleId: z.string().uuid() }).parse(raw))
  .handler(async ({ data }): Promise<CirclePost[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: rows, error } = await supa
      .from("circle_posts")
      .select("id, title, body, author_display_name, created_at")
      .eq("circle_id", data.circleId)
      .eq("is_hidden", false)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const joinCircle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ circleId: z.string().uuid() }).parse(raw))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("circle_members").insert({
      circle_id: data.circleId,
      user_id: context.userId,
    });
    // Ignore unique violation — already a member
    if (error && !error.message.includes("duplicate")) throw new Error(error.message);
    return { ok: true };
  });

export const isCircleMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ circleId: z.string().uuid() }).parse(raw))
  .handler(async ({ data, context }): Promise<boolean> => {
    const { data: row } = await context.supabase
      .from("circle_members")
      .select("id")
      .eq("circle_id", data.circleId)
      .eq("user_id", context.userId)
      .maybeSingle();
    return Boolean(row);
  });

export const createCirclePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z.object({
      circleId: z.string().uuid(),
      title: z.string().trim().max(200).optional(),
      body: z.string().trim().min(1).max(8000),
    }).parse(raw)
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("circle_posts").insert({
      circle_id: data.circleId,
      user_id: context.userId,
      title: data.title ?? null,
      body: data.body,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
